import React, { useId } from 'react';
import { childKey, queryTokens } from '../../Nav/navSearch';

const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Wrap every query token occurrence in <mark>, returning React nodes (not an HTML string). */
export function highlightTokens(text, tokens) {
  if (!tokens.length || !text) {
    return text;
  }
  const pattern = new RegExp(`(${tokens.map(escapeRegex).join('|')})`, 'gi');
  // String.split with a capturing group puts the captured matches at the odd indexes.
  return String(text)
    .split(pattern)
    .map((part, index) => (
      index % 2 === 1 ? <mark key={index}>{part}</mark> : part
    ));
}

export default function SellerNavItem({
  item,
  isExpanded,
  onToggle,
  searchResult,
  isSearching,
  query,
  active,
  counts,
  alerts,
}) {
  const childrenId = useId();
  const tokens = queryTokens(query);
  const hasChildren = Boolean(item.children && item.children.length);
  const hasNotes = Boolean(item.contextualNotes && item.contextualNotes.length);
  // Notes are collapsed behind the same disclosure as children, so an item with only notes
  // still gets a toggle.
  const hasSubEntries = hasChildren || hasNotes;
  const isActive = active?.itemId === item.id && !active?.childId;
  const isActiveTrail = active?.itemId === item.id;
  const count = item.alertKey ? counts?.[item.alertKey] || 0 : 0;
  const alertCount = item.alertKey ? alerts?.[item.alertKey] || 0 : 0;
  const hasUpdatedCount = alertCount > 0;

  return (
    <li
      className={[
        'seller-nav-item',
        isActive ? 'seller-nav-item--active' : '',
        isActiveTrail ? 'seller-nav-item--active-trail' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="seller-nav-item__row">
        <a
          href={item.href}
          className="seller-nav-item__link"
          aria-current={isActive ? 'page' : undefined}
          {...(item.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          <span className="seller-nav-item__icon" aria-hidden="true">
            <i className={item.icon}></i>
          </span>
          <span className="seller-nav-item__text">
            <span className="seller-nav-item__title-row">
              <strong>{highlightTokens(item.title, tokens)}</strong>
              {item.alertKey && count > 0 && (
                <span
                  className={[
                    'seller-nav-item__count-badge',
                    hasUpdatedCount ? 'seller-nav-item__count-badge--updated' : '',
                  ].filter(Boolean).join(' ')}
                  aria-label={
                    hasUpdatedCount ? `${count} total, ${alertCount} new` : `${count} total`
                  }
                >
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </span>
            <span className="seller-nav-item__description">
              {highlightTokens(item.description, tokens)}
            </span>
          </span>
        </a>
        {hasSubEntries && (
          <button
            type="button"
            className="seller-nav-item__disclosure"
            aria-expanded={isExpanded}
            aria-controls={childrenId}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} ${item.title} shortcuts`}
            onClick={onToggle}
          >
            <i className="fa-solid fa-chevron-down" aria-hidden="true"></i>
          </button>
        )}
      </div>

      {hasChildren && isExpanded && (
        <ul className="seller-nav-item__children" id={childrenId}>
          {item.children.map(child => {
            const isChildActive = active?.itemId === item.id && active?.childId === child.id;
            const isChildDimmed = isSearching
              && !searchResult.matchingChildIds.has(childKey(item.id, child.id));
            return (
              <li
                key={child.id}
                className={[
                  'seller-nav-item__child',
                  isChildActive ? 'seller-nav-item__child--active' : '',
                  isChildDimmed ? 'seller-nav-item__child--dimmed' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <a
                  href={child.href}
                  className="seller-nav-item__child-link"
                  aria-current={isChildActive ? 'page' : undefined}
                >
                  <span className="seller-nav-item__child-icon" aria-hidden="true">
                    <i className={child.icon}></i>
                  </span>
                  <span className="seller-nav-item__child-text">
                    <strong>{highlightTokens(child.title, tokens)}</strong>
                    <span className="seller-nav-item__child-description">
                      {highlightTokens(child.description, tokens)}
                    </span>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      )}

      {hasNotes && isExpanded && (
        <ul className="seller-nav-item__contextual-notes" id={hasChildren ? undefined : childrenId}>
          {item.contextualNotes.map(note => (
            <li key={note.label} className="seller-nav-item__contextual-note">
              <span aria-disabled="true" title={note.label}>
                {note.label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
